import React, { useReducer, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form, ListGroup } from 'react-bootstrap';

// Reducer function for counter
const counterReducer = (state, action) => {
  console.log('🔄 useReducer: Action dispatched', action);
  console.log('   Current state:', state);
  
  switch (action.type) {
    case 'INCREMENT':
      return { count: state.count + 1 };
    case 'DECREMENT':
      return { count: state.count - 1 };
    case 'RESET':
      return { count: 0 };
    case 'SET':
      return { count: action.payload };
    default:
      return state;
  }
};

// Reducer for todo list
const todoReducer = (state, action) => {
  console.log('📝 Todo Reducer: Action', action.type);
  
  switch (action.type) {
    case 'ADD_TODO':
      return [...state, { id: Date.now(), text: action.payload, completed: false }];
    case 'TOGGLE_TODO':
      return state.map(todo =>
        todo.id === action.payload ? { ...todo, completed: !todo.completed } : todo
      );
    case 'DELETE_TODO':
      return state.filter(todo => todo.id !== action.payload);
    default:
      return state;
  }
};

const Lesson11_UseReducer = () => {
  const [state, dispatch] = useReducer(counterReducer, { count: 0 });
  const [todos, todoDispatch] = useReducer(todoReducer, []);
  const [todoInput, setTodoInput] = React.useState('');

  useEffect(() => {
    console.log('📖 Lesson 11: useReducer Hook - Loaded');
    console.log('📚 useReducer is great for complex state logic');
  }, []);

  const addTodo = () => {
    if (todoInput.trim()) {
      todoDispatch({ type: 'ADD_TODO', payload: todoInput });
      setTodoInput('');
    }
  };

  return (
    <LessonLayout 
      lessonNumber={11}
      title="useReducer Hook" 
      description="Manage complex state logic with useReducer"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>useReducer hook basics</li>
          <li>Reducer function pattern</li>
          <li>Actions and dispatch</li>
          <li>When to use useReducer vs useState</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Counter with useReducer</Card.Header>
            <Card.Body className="text-center">
              <h2 className="display-3">{state.count}</h2>
              <div className="d-flex gap-2 justify-content-center flex-wrap">
                <Button onClick={() => dispatch({ type: 'INCREMENT' })} variant="success">
                  Increment
                </Button>
                <Button onClick={() => dispatch({ type: 'DECREMENT' })} variant="warning">
                  Decrement
                </Button>
                <Button onClick={() => dispatch({ type: 'RESET' })} variant="secondary">
                  Reset
                </Button>
                <Button onClick={() => dispatch({ type: 'SET', payload: 100 })} variant="primary">
                  Set to 100
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Todo List with useReducer</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Add a todo..."
                    value={todoInput}
                    onChange={(e) => setTodoInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                  />
                  <Button onClick={addTodo}>Add</Button>
                </div>
              </Form.Group>
              <ListGroup>
                {todos.map(todo => (
                  <ListGroup.Item key={todo.id} className="d-flex justify-content-between align-items-center">
                    <span
                      style={{ textDecoration: todo.completed ? 'line-through' : 'none', cursor: 'pointer' }}
                      onClick={() => todoDispatch({ type: 'TOGGLE_TODO', payload: todo.id })}
                    >
                      {todo.text}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => todoDispatch({ type: 'DELETE_TODO', payload: todo.id })}
                    >
                      Delete
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Header>useReducer Pattern</Card.Header>
        <Card.Body>
          <pre className="code-example">
{`// 1. Define reducer function
const reducer = (state, action) => {
  switch (action.type) {
    case 'INCREMENT':
      return { count: state.count + 1 };
    case 'DECREMENT':
      return { count: state.count - 1 };
    default:
      return state;
  }
};

// 2. Use in component
const [state, dispatch] = useReducer(reducer, { count: 0 });

// 3. Dispatch actions
dispatch({ type: 'INCREMENT' });`}
          </pre>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 useState vs useReducer:</strong>
        <ul className="mb-0">
          <li><strong>useState:</strong> Simple state, independent state variables</li>
          <li><strong>useReducer:</strong> Complex state logic, related state updates, multiple sub-values</li>
          <li>useReducer helps with testing and debugging complex state transitions</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson11_UseReducer;
